/**
 * Call outcome templates and the pure rules behind `CallOutcomeCapture`: two taps (reached / not reached, then an
 * outcome from the template), a note, a callback time when the outcome asks for one, and a do-not-call confirm
 * when the outcome writes a suppression.
 *
 * The templates come from the backend taxonomy (`GET /call-missions/outcomes`, read with `useOutcomeTemplate` in
 * `services/call-missions.ts`): labels are Title Case because each outcome is a button. The memberships renewal
 * log keeps its own template (`memberships/renewal-call-outcomes.ts`): its values are that log's Contact Status.
 */
import { format } from "date-fns";

import { parseLocalDate } from "@/lib/local-date";

export type CallReach = "reached" | "not_reached";

/** What RingCentral reported when the call ended (the click-to-dial terminal phase). */
export type MachineCallOutcome = "reached" | "voicemail" | "other";

export interface OutcomeOption {
  value: string;
  label: string;
  /** The rep picks a callback date and time. */
  callback?: boolean;
  /** The outcome stops future calls: the rep confirms the do-not-call. */
  dnc?: boolean;
  /** Pre-selected when RingCentral reports the call went to voicemail. */
  voicemail?: boolean;
}

export interface OutcomeTemplate {
  reached: OutcomeOption[];
  not_reached: OutcomeOption[];
}

export interface CallOutcomeValue {
  reach: CallReach | null;
  outcome: string | null;
  note: string;
  /** Business-local `YYYY-MM-DD` and 24-hour `HH:MM`; empty until picked. */
  callbackDate: string;
  callbackTime: string;
  dncConfirmed: boolean;
}

export const EMPTY_CALL_OUTCOME: CallOutcomeValue = {
  reach: null,
  outcome: null,
  note: "",
  callbackDate: "",
  callbackTime: "",
  dncConfirmed: false,
};

export const REACH_LABELS: Record<CallReach, string> = { reached: "Reached", not_reached: "Not Reached" };

/** No outcomes: a capture that asks for the note alone (`steps="note"`). */
export const NOTE_ONLY: OutcomeTemplate = { reached: [], not_reached: [] };

export function optionFor(template: OutcomeTemplate, value: CallOutcomeValue): OutcomeOption | null {
  if (!value.reach || !value.outcome) return null;
  return template[value.reach].find((o) => o.value === value.outcome) ?? null;
}

/** The capture pre-filled from what RingCentral saw: answered → Reached, voicemail → Not reached + voicemail. */
export function machineSuggestion(template: OutcomeTemplate, machine: MachineCallOutcome | null): CallOutcomeValue {
  if (machine === "reached") return { ...EMPTY_CALL_OUTCOME, reach: "reached" };
  if (machine === "voicemail") {
    const vm = template.not_reached.find((o) => o.voicemail);
    return { ...EMPTY_CALL_OUTCOME, reach: "not_reached", outcome: vm?.value ?? null };
  }
  return EMPTY_CALL_OUTCOME;
}

/** Every tap the template asks for is made: the reach, an outcome when that side has any, a callback time, a DNC confirm. */
export function outcomeReady(template: OutcomeTemplate, value: CallOutcomeValue): boolean {
  if (!value.reach) return false;
  if (template[value.reach].length === 0) return true;
  const option = optionFor(template, value);
  if (!option) return false;
  if (option.callback && !(value.callbackDate && value.callbackTime)) return false;
  if (option.dnc && !value.dncConfirmed) return false;
  return true;
}

/** "Mon Nov 3, 2:00 PM" for a picked callback, or null. */
export function callbackLabel(value: CallOutcomeValue): string | null {
  if (!value.callbackDate || !value.callbackTime) return null;
  const [h, m] = value.callbackTime.split(":").map(Number);
  const day = parseLocalDate(value.callbackDate);
  day.setHours(h, m, 0, 0);
  return format(day, "EEE MMM d, h:mm a");
}

/** One line for a to-do summary or a log: "Reached · Call back Mon Nov 3, 2:00 PM. Note: …". */
export function describeCallOutcome(template: OutcomeTemplate, value: CallOutcomeValue): string {
  const parts = [value.reach ? REACH_LABELS[value.reach] : null];
  const option = optionFor(template, value);
  if (option) {
    const when = option.callback ? callbackLabel(value) : null;
    parts.push(when ? `${option.label} ${when}` : option.label);
  }
  const head = `Call: ${parts.filter(Boolean).join(" · ")}.`;
  const note = value.note.trim();
  return note ? `${head} Note: ${note}` : head;
}
